import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Bytes } from '@/components/Bytes'

import { EmptyValue } from '@/components/EmptyValue'
import { LocalDate } from '@/components/LocalDate'
import { PageHeader } from '@/components/PageHeader'
import { Badge } from '@/components/ui/badge'
import { listAllOrganizations } from '@/lib/admin/server'
import { AdminListCard } from './-components/AdminListCard'

export const Route = createFileRoute('/_authenticated/admin/organizations')({
  component: AdminOrganizationsPage,
})

type OrgRow = Awaited<ReturnType<typeof listAllOrganizations>>[number]

const columns: ColumnDef<OrgRow>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    enableSorting: true,
    cell: ({ row }) => {
      const org = row.original
      return (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <Link
              to="/admin/organizations/$orgId"
              params={{ orgId: org.id }}
              className="font-medium hover:underline"
            >
              {org.name}
            </Link>
            {org.deletedAt ? <Badge variant="secondary">Deleted</Badge> : null}
          </div>
          <div className="text-xs text-muted-foreground">{org.slug}</div>
        </div>
      )
    },
  },
  {
    accessorKey: 'memberCount',
    header: 'Members',
    enableSorting: true,
    cell: ({ row }) => (
      <span className="tabular-nums">{row.original.memberCount}</span>
    ),
  },
  {
    accessorKey: 'credentialCount',
    header: 'Credentials',
    enableSorting: true,
    cell: ({ row }) => (
      <span className="tabular-nums">{row.original.credentialCount}</span>
    ),
  },
  {
    accessorKey: 'planSlug',
    header: 'Plan',
    enableSorting: true,
    cell: ({ row }) =>
      row.original.planSlug ? (
        <Badge variant="secondary">{row.original.planSlug}</Badge>
      ) : (
        <EmptyValue>No plan</EmptyValue>
      ),
  },
  {
    accessorKey: 'balanceBytes',
    header: 'Balance',
    enableSorting: true,
    cell: ({ row }) => (
      <span className="flex items-center gap-2 tabular-nums">
        <Bytes value={row.original.balanceBytes} unit="gb" />
        {row.original.overageBytes > 0 ? (
          <Badge variant="destructive">overage</Badge>
        ) : null}
      </span>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    enableSorting: true,
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        <LocalDate iso={row.original.createdAt} />
      </span>
    ),
  },
]

function AdminOrganizationsPage() {
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Organizations"
        description="Every organization on the platform."
      />
      <AdminListCard
        title="Organizations"
        noun="organization"
        queryKey={['admin-organizations']}
        queryFn={() => listAllOrganizations()}
        columns={columns}
        emptyMessage="No organizations yet."
        searchAccessor={(o) => `${o.name} ${o.slug}`}
        searchPlaceholder="Search by name or slug"
      />
    </div>
  )
}
